import React, { useState } from 'react';

import { getRepositoryRoute, link } from '@aline/routes';
import { Card, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from 'src/auth';
import { AlineAvatar, AlineTooltip, NumCopies, Spacer } from 'src/components';
import { AlineLogoIcon } from 'src/components/icons/AlineLogoIcon';
import { CompletedIcon } from 'src/components/icons/CompletedIcon';
import { DownloadIcon } from 'src/components/icons/DownloadIcon';
import { useCurrentUserId } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { useDispatchButtonSnack } from 'src/helpers';
import { openLink } from 'src/helpers/url';
import { useImportTemplate } from 'src/hooks/workflow/community';
import { createClampStyles } from 'src/theme';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const titleMaxLines = 2;
export const descriptionMaxLines = 3;

const useStyles = makeStyles()(theme => ({
  card: {
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: 320,
  },
  cardHeader: {
    alignItems: 'center',
    display: 'flex',
  },
  cardTitleText: {
    ...createClampStyles(titleMaxLines),
  },
  completedIcon: {
    color: theme.appColors.accent2,
  },
  description: {
    flexGrow: 1,
  },
  descriptionText: {
    ...createClampStyles(descriptionMaxLines),
    color: theme.appColors.placeholderType,
    fontSize: 12,
    fontWeight: 400,
  },
  owner: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.25),
    // So the owner container doesn't jump when the import progress spinner renders.
    height: 40,
  },
}));

export interface CommunityTemplateCardTemplate extends Pick<
  Apollo.Document,
  'description' | 'id' | 'name' | 'numTimesImported' | 'requesterImportedDocumentIds' | '__typename'
> {
  owner: UserSubset;
}

export function CommunityTemplateCard({
  template,
}: {
  template: CommunityTemplateCardTemplate;
}): JSX.Element | null {
  const { classes } = useStyles();
  const [raised, setRaised] = useState<boolean>(false);

  return (
    <Card
      className={classes.card}
      raised={raised}
      onClick={() =>
        openLink(link.DocumentDetail({ documentId: template.id }), { title: template.name })
      }
      onMouseEnter={() => setRaised(true)}
      onMouseLeave={() => setRaised(false)}
    >
      <div className={classes.cardHeader}>
        <AlineLogoIcon />
        <Spacer />
        <NumCopies numCopies={template.numTimesImported} />
      </div>
      <Typography className={classes.cardTitleText} variant="subtitle1">
        {template.name || 'Untitled'}
      </Typography>
      <div className={classes.description}>
        <Typography className={classes.descriptionText}>
          {template.description || 'No description'}
        </Typography>
      </div>
      <div className={classes.owner}>
        <AlineAvatar avatarInfo={template.owner} />
        <Typography variant="body1">{template.owner.displayName}</Typography>
        <Spacer />
        <ImportButton template={template} />
      </div>
    </Card>
  );
}

export function CommunityTemplateLoadingCard(): JSX.Element {
  const { classes } = useStyles();

  return (
    <Card className={classes.card} style={{ maxHeight: 304 }}>
      <div className={classes.cardHeader}>
        <Skeleton height={32} variant="circular" width={32} />
        <Spacer />
        <Typography>
          <Skeleton variant="text" width={48} />
        </Typography>
      </div>
      <Typography className={classes.cardTitleText}>
        <Skeleton variant="text" width={144} />
      </Typography>
      <div className={classes.description}>
        {Array.from({ length: 2 }).map((_v, index) => {
          return (
            <Typography key={index} className={classes.descriptionText}>
              <Skeleton variant="text" width={200} />
            </Typography>
          );
        })}
      </div>
      <div className={classes.owner}>
        <Skeleton height={32} variant="circular" width={32} />
        <Skeleton variant="text" width={80} />
      </div>
    </Card>
  );
}

interface ImportButtonTemplate extends Pick<
  Apollo.Document,
  'id' | 'requesterImportedDocumentIds' | '__typename'
> {
  owner: Pick<Apollo.User, 'id' | '__typename'>;
}

function ImportButton({ template }: { template: ImportButtonTemplate }) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const currentUserId = useCurrentUserId();
  const { importTemplate, loading: importingTemplate } = useImportTemplate();
  const dispatchSuccessSnack = useDispatchButtonSnack<Pick<Apollo.Document, 'id' | '__typename'>>({
    autoHideDurationMS: 7500,
    buttonText: 'Open',
    message: 'Template successfully imported.',
    onClick: (e, importedTemplate) => {
      e.stopPropagation();
      if (importedTemplate) openLink(link.DocumentDetail({ documentId: importedTemplate.id }));
    },
  });
  const { guardedCallback: handleImportTemplate } = useAuthGuard(async () => {
    const { data } = await importTemplate(template.id);
    const importedTemplate = data?.duplicateTemplate.document;
    if (importedTemplate) dispatchSuccessSnack(importedTemplate);
  });
  const isOwner = template.owner.id === currentUserId;
  const requesterAlreadyImported = isOwner || Boolean(template.requesterImportedDocumentIds.length);

  if (importingTemplate) return <CircularProgress color="primary" size={20} />;

  if (requesterAlreadyImported)
    return (
      <AlineTooltip arrow placement="top" title="View imported template">
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            navigate(getRepositoryRoute({ filter: 'templates' }));
          }}
        >
          <CompletedIcon className={classes.completedIcon} />
        </IconButton>
      </AlineTooltip>
    );

  return (
    <AlineTooltip arrow placement="top" title="Import">
      <IconButton
        size="small"
        onClick={async e => {
          e.stopPropagation();
          await handleImportTemplate();
        }}
      >
        <DownloadIcon />
      </IconButton>
    </AlineTooltip>
  );
}
